import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, RefreshCcw, TrendingUp, DollarSign, CheckCircle, Clock, XCircle, CreditCard } from 'lucide-react';
import alertService from '../services/alertService';
import vendaService from '../services/vendaService';
import productService from '../services/productService';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from 'recharts';
import BarChartWithMinHeight from '../components/ui/BarChartWithMinHeight';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

// Formata datas vindas como ISO (YYYY-MM-DD) ou BR (DD/MM/YYYY)
const formatDate = (value?: string) => {
  if (!value) return '';
  try {
    let d: Date;
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      d = new Date(value);
    } else if (/^\d{2}\/\d{2}\/\d{4}/.test(value)) {
      const [dd, mm, yyyy] = value.split('/');
      d = new Date(`${yyyy}-${mm}-${dd}`);
    } else {
      d = new Date(value);
    }
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('pt-BR');
  } catch {
    return value || '';
  }
};

const toYmd = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const defaultStart = () => {
  const d = new Date();
  d.setDate(1);
  return toYmd(d);
};

const SalesDashboardPage: React.FC = () => {
  const [dataInicial, setDataInicial] = useState<string>(defaultStart());
  const [dataFinal, setDataFinal] = useState<string>(toYmd(new Date()));
  const [loading, setLoading] = useState<boolean>(false);

  // Raw API results
  const [byWeekday, setByWeekday] = useState<any[]>([]);
  const [byMonth, setByMonth] = useState<any[]>([]);
  const [topProducts, setTopProducts] = useState<any[]>([]);
  const [topProductsReady, setTopProductsReady] = useState<{ label: string; valor: number }[]>([]);
  const [dashboardData, setDashboardData] = useState<any>({});
  const productNameCacheRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    try {
      const raw = localStorage.getItem('productNameCache');
      if (raw) {
        const entries = JSON.parse(raw);
        if (Array.isArray(entries)) {
          productNameCacheRef.current = new Map(entries as [string, string][]);
        }
      }
    } catch {}
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { dataInicial, dataFinal };
      const ano = new Date(dataFinal || new Date()).getFullYear();
      const [rWeek, rMonth, rTop, rDash] = await Promise.all([
        vendaService.vendasPorDiaSemana(params),
        vendaService.vendasPorMes({ ano }),
        vendaService.produtosMaisVendidos({ ...params, limit: 10 }),
        vendaService.obterDashboard(params),
      ]);

      const rawWeek = (rWeek?.data?.data ?? rWeek?.data) || [];
      const rawMonth = (rMonth?.data?.data ?? rMonth?.data) || [];
      const rawTop = (rTop?.data?.data ?? rTop?.data) || [];
      const rawDash = (rDash?.data?.data ?? rDash?.data) || {};

      const objToArray = (o: any) => {
        if (Array.isArray(o)) return o;
        if (o && typeof o === 'object') return Object.entries(o).map(([k, v]) => ({ nome: k, total: v }));
        return [] as any[];
      };

      const weekArr = objToArray(rawWeek);
      const monthArr = objToArray(rawMonth);
      const topArr = objToArray(rawTop);

      setByWeekday(weekArr);
      setByMonth(monthArr);
      setTopProducts(topArr);
      setDashboardData(rawDash);

      // Resolver nomes dos produtos (quando vier apenas IDs)
      const normalizedTop = topArr.map((item: any) => {
        if (Array.isArray(item)) {
          const [first, a, b] = item;
          const produtoId = String(first);
          const total = Number(typeof b === 'number' ? b : (typeof a === 'number' ? a : 0));
          return { produtoId, produtoNome: undefined as string | undefined, total };
        }
        const produtoId = (item?.produtoId ?? item?.produto?.id ?? item?.produto ?? item?.id ?? item?.nome);
        const produtoNome = (item?.produtoNome ?? item?.nome ?? item?.descricao ?? undefined);
        const total = Number(item?.total ?? item?.qtd ?? item?.quantidade ?? item?.valorTotal ?? item?.valor ?? 0);
        return { produtoId: produtoId ? String(produtoId) : undefined, produtoNome, total };
      });

      const ids = Array.from(new Set(normalizedTop.map(i => i.produtoId).filter(Boolean))) as string[];
      const nameMap = new Map<string, string>();
      for (const i of normalizedTop) {
        if (i.produtoId && i.produtoNome) nameMap.set(i.produtoId, i.produtoNome);
      }
      const toFetch = ids.filter((id) => !nameMap.has(id) && !productNameCacheRef.current.has(id));
      if (toFetch.length > 0) {
        await Promise.all(toFetch.map(async (id) => {
          try {
            const resp = await productService.getById(id);
            const nome = resp?.data?.nome || (resp as any)?.data?.descricao || undefined;
            if (nome) {
              nameMap.set(id, nome);
              productNameCacheRef.current.set(id, nome);
            }
          } catch (err) {
          }
        }));
        try {
          localStorage.setItem('productNameCache', JSON.stringify(Array.from(productNameCacheRef.current.entries())));
        } catch {}
      }
      for (const id of ids) {
        if (!nameMap.has(id) && productNameCacheRef.current.has(id)) {
          nameMap.set(id, productNameCacheRef.current.get(id)!);
        }
      }

      const ready = normalizedTop.map(i => ({
        label: i.produtoNome || (i.produtoId ? (nameMap.get(i.produtoId) || i.produtoId) : 'Produto'),
        valor: i.total,
      }));
      setTopProductsReady(ready);
    } catch (e: any) {
      alertService.error('Falha ao carregar dados do dashboard', e?.message || 'Verifique a API \/api\/v3\/api-docs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Normalizers to handle different key names from API
  const weekdayData = useMemo(() => {
    const order = ['DOMINGO', 'SEGUNDA','TERCA','QUARTA','QUINTA','SEXTA','SABADO'];
    const mapEnToPt: Record<string, string> = {
      SUNDAY: 'DOMINGO', MONDAY: 'SEGUNDA', TUESDAY: 'TERCA', WEDNESDAY: 'QUARTA', THURSDAY: 'QUINTA', FRIDAY: 'SEXTA', SATURDAY: 'SABADO'
    };
    const numToPt: Record<string, string> = {
      '1': 'DOMINGO', '2': 'SEGUNDA', '3': 'TERCA', '4': 'QUARTA', '5': 'QUINTA', '6': 'SEXTA', '7': 'SABADO'
    };
    const normalize = (x: any) => {
      // Suporta tuplas [label, valor] ou [label, qtd, valor]
      if (Array.isArray(x)) {
        const [lab, a, b] = x;
        const label = String(lab).toUpperCase();
        let labelUp = label;
        if (mapEnToPt[labelUp]) labelUp = mapEnToPt[labelUp];
        if (numToPt[labelUp]) labelUp = numToPt[labelUp];
        const valor = Number(typeof b === 'number' ? b : (typeof a === 'number' ? a : 0));
        return { label: labelUp, valor };
      }
      let labelRaw = x?.label ?? x?.diaSemana ?? x?.weekday ?? x?.dia ?? x?.nome ?? x?.descricao ?? x?.dayOfWeek ?? x?.dia_da_semana ?? x?.diaSemanaNome ?? x?.nomeDia;
      if (labelRaw === undefined || labelRaw === null) labelRaw = 'N/D';
      let labelUp = String(labelRaw).toUpperCase();
      if (mapEnToPt[labelUp]) labelUp = mapEnToPt[labelUp];
      if (numToPt[labelUp]) labelUp = numToPt[labelUp];
      const valor = Number(x?.total ?? x?.valorTotal ?? x?.valor ?? x?.qtd ?? x?.quantidade ?? x?.count ?? x?.totalVendas ?? 0);
      return { label: labelUp, valor };
    };
    const mapped = byWeekday.map(normalize);
    return mapped.sort((a, b) => (order.indexOf(a.label) - order.indexOf(b.label)) || 0);
  }, [byWeekday]);

  const monthData = useMemo(() => {
    const monthsPt = ['01','02','03','04','05','06','07','08','09','10','11','12'];
    const normalize = (x: any) => {
      // Suporta tuplas [mes, valor] ou [mes, qtd, valor]
      if (Array.isArray(x)) {
        let [m, a, b] = x;
        let label = String(m);
        // Ajusta rótulo numérico para MM/AAAA quando possível
        if (/^\d{1,2}$/.test(label)) {
          const mm = monthsPt[(Number(label) - 1) % 12] || label.padStart(2, '0');
          const ano = String((new Date().getFullYear()));
          label = `${mm}/${ano}`;
        }
        const isoMatch = /^(\d{4})-(\d{2})$/.exec(label);
        const compact = /^(\d{6})$/.exec(label);
        if (isoMatch) label = `${isoMatch[2]}/${isoMatch[1]}`;
        else if (compact) label = `${compact[1].substring(4,6)}/${compact[1].substring(0,4)}`;
        const valor = Number(typeof b === 'number' ? b : (typeof a === 'number' ? a : 0));
        return { label, valor };
      }
      const m = x?.mesNome ?? x?.mes ?? x?.month ?? x?.anoMes ?? x?.periodo ?? x?.nome ?? x?.descricao ?? 'N/D';
      const valor = Number(x?.total ?? x?.valorTotal ?? x?.valor ?? x?.qtd ?? x?.quantidade ?? x?.count ?? 0);
      let label = String(m);
      if (/^\d{1,2}$/.test(label)) {
        const mm = monthsPt[(Number(label) - 1) % 12] || label.padStart(2, '0');
        const ano = String((x?.ano ?? new Date().getFullYear()));
        label = `${mm}/${ano}`;
      }
      const isoMatch = /^(\d{4})-(\d{2})$/.exec(label);
      const compact = /^(\d{6})$/.exec(label);
      if (isoMatch) label = `${isoMatch[2]}/${isoMatch[1]}`;
      else if (compact) label = `${compact[1].substring(4,6)}/${compact[1].substring(0,4)}`;
      return { label, valor };
    };
    return byMonth.map(normalize);
  }, [byMonth]);

  const productData = useMemo(() => topProductsReady, [topProductsReady]);

  // Gráfico de pizza: vendas por status
  const statusPieData = useMemo(() => {
    const statsObj = (dashboardData?.vendasPorStatus || dashboardData?.salesByStatus || null) as any;
    const baseStats = (dashboardData?.estatisticas || dashboardData?.stats || {}) as any;
    const statuses = ['CANCELADA','EM_PROCESSAMENTO','ENTREGUE','FINALIZADA','PARCIALMENTE_ENTREGUE','PENDENTE'];
    const getNum = (v: any) => (typeof v === 'number' ? v : Number(v ?? 0));
    const fromStats = {
      FINALIZADA: getNum(baseStats?.vendasFinalizadas),
      PENDENTE: getNum(baseStats?.vendasPendentes),
      CANCELADA: getNum(baseStats?.vendasCanceladas),
    } as Record<string, number>;
    const obj = statsObj && typeof statsObj === 'object' ? statsObj : fromStats;
    return statuses.map(label => ({ label, valor: getNum(obj?.[label]) }));
  }, [dashboardData]);

  const statusColors: Record<string, string> = {
    CANCELADA: '#ef4444',
    EM_PROCESSAMENTO: '#f59e0b',
    ENTREGUE: '#14b8a6',
    FINALIZADA: '#10b981',
    PARCIALMENTE_ENTREGUE: '#6366f1',
    PENDENTE: '#9ca3af',
  };

  // Vendas recentes: normalizar possíveis formatos vindos da API
  const recentSales = useMemo(() => {
    const raw = ((dashboardData?.vendasRecentes ?? dashboardData?.recentSales) || []) as any[];
    const parseObj = (x: any) => {
      const id = x?.id ?? x?.codigo ?? x?.vendaId ?? x?.numero ?? x?.sequencia;
      const cliente = x?.clienteNome ?? x?.cliente ?? x?.nomeCliente;
      const vendedor = x?.vendedorNome ?? x?.vendedor ?? x?.nomeVendedor;
      const valorTotal = Number(x?.valorTotal ?? x?.total ?? x?.valor ?? 0);
      const status = x?.status ?? x?.situacao ?? x?.estado;
      const dataVendaRaw = x?.dataVenda ?? x?.createdAt ?? x?.data ?? x?.emissao ?? x?.dt;
      return {
        id: id ? String(id) : undefined,
        cliente: cliente ?? undefined,
        vendedor: vendedor ?? undefined,
        valorTotal,
        status: status ? String(status) : undefined,
        dataVenda: dataVendaRaw ? formatDate(String(dataVendaRaw)) : undefined,
      };
    };
    const parseArr = (arr: any[]) => {
      const numIdx = arr.findIndex((v: any) => typeof v === 'number');
      const totalRaw = numIdx >= 0 ? arr[numIdx] : 0;
      const statusRaw = arr.find((v: any) => typeof v === 'string' && /(FINALIZADA|PENDENTE|CANCELADA)/i.test(v));
      const dateRaw = arr.find((v: any) => typeof v === 'string' && (/^\d{4}-\d{2}-\d{2}/.test(v) || /^\d{2}\/\d{2}\/\d{4}/.test(v)));
      const strings = arr.filter((v: any) => typeof v === 'string');
      const idCandidate = strings.find((s: string) => /^\d+$/.test(s)) ?? arr[0];
      const names = strings.filter((s: string) => !(/(FINALIZADA|PENDENTE|CANCELADA)/i.test(s)) && !(/^\d{4}-\d{2}-\d{2}/.test(s)) && !(/^\d{2}\/\d{2}\/\d{4}/.test(s)));
      const cliente = names[0];
      const vendedor = names[1];
      return {
        id: idCandidate !== undefined ? String(idCandidate) : undefined,
        cliente,
        vendedor,
        valorTotal: Number(totalRaw || 0),
        status: statusRaw ? String(statusRaw) : undefined,
        dataVenda: dateRaw ? formatDate(String(dateRaw)) : undefined,
      };
    };
    return raw.map((x: any) => Array.isArray(x) ? parseArr(x) : parseObj(x));
  }, [dashboardData]);

  // Construir cards do dashboard a partir de dashboard.estatisticas (preferencial)
  const dashboardCards = useMemo(() => {
    const stats = (dashboardData?.estatisticas || dashboardData?.stats || null) as any;
    if (stats && typeof stats === 'object') {
      const cards: { label: string; value: string }[] = [];
      const safeNum = (v: any) => (typeof v === 'number' ? v : Number(v ?? 0));
      // Ordem e labels amigáveis
      const valorTotalVendas = safeNum(stats.valorTotalVendas);
      const totalVendas = safeNum(stats.totalVendas);
      const ticketMedio = safeNum(stats.ticketMedio);
      const vendasFinalizadas = safeNum(stats.vendasFinalizadas);
      const vendasPendentes = safeNum(stats.vendasPendentes);
      const vendasCanceladas = safeNum(stats.vendasCanceladas);

      cards.push({ label: 'Valor Total de Vendas', value: formatCurrency(valorTotalVendas) });
      cards.push({ label: 'Total de Vendas', value: String(totalVendas) });
      cards.push({ label: 'Ticket Médio', value: formatCurrency(ticketMedio) });
      cards.push({ label: 'Vendas Finalizadas', value: String(vendasFinalizadas) });
      cards.push({ label: 'Vendas Pendentes', value: String(vendasPendentes) });
      cards.push({ label: 'Vendas Canceladas', value: String(vendasCanceladas) });
      return cards;
    }

    // Fallback: criar cards genéricos a partir do objeto base
    const entries = Object.entries(dashboardData || {});
    const cards: { label: string; value: string }[] = [];
    const prettify = (k: string) => k.replace(/[_.-]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/(^|\s)\w/g, (m) => m.toUpperCase());
    const isCurrencyKey = (k: string) => /valor|faturamento|receita|lucro|total|ticket/i.test(k);
    for (const [key, val] of entries) {
      if (typeof val === 'number') {
        cards.push({ label: prettify(key), value: isCurrencyKey(key) ? formatCurrency(val) : String(val) });
      } else if (val && typeof val === 'object') {
        const num = (val as any).valor ?? (val as any).total ?? (val as any).qtd ?? (val as any).quantidade;
        if (typeof num === 'number') {
          cards.push({ label: prettify(key), value: isCurrencyKey(key) ? formatCurrency(num) : String(num) });
        }
      }
      if (cards.length >= 6) break; // limitar a quantidade inicial de cards
    }
    return cards;
  }, [dashboardData]);

  // Estilo visual dos cards (ícones e gradientes) baseado no label
  const getCardVisual = (label: string) => {
    const l = (label || '').toLowerCase();
    // Usar cores padrão (green/yellow) para garantir disponibilidade no Tailwind v2
    if (l.includes('valor total')) return { Icon: DollarSign, gradient: 'from-green-500 to-green-600', bgGradient: 'from-green-50 to-green-100' };
    if (l.includes('total de vendas')) return { Icon: TrendingUp, gradient: 'from-blue-500 to-blue-600', bgGradient: 'from-blue-50 to-blue-100' };
    if (l.includes('ticket')) return { Icon: CreditCard, gradient: 'from-purple-500 to-purple-600', bgGradient: 'from-purple-50 to-purple-100' };
    if (l.includes('finalizadas')) return { Icon: CheckCircle, gradient: 'from-green-500 to-green-600', bgGradient: 'from-green-50 to-green-100' };
    if (l.includes('pendentes')) return { Icon: Clock, gradient: 'from-yellow-500 to-yellow-600', bgGradient: 'from-yellow-50 to-yellow-100' };
    if (l.includes('canceladas')) return { Icon: XCircle, gradient: 'from-red-500 to-red-600', bgGradient: 'from-red-50 to-red-100' };
    return { Icon: TrendingUp, gradient: 'from-gray-400 to-gray-500', bgGradient: 'from-gray-50 to-gray-100' };
  };

  return (
    <div>
      {/* Header estilo DashboardPage */}
      <div className="bg-gradient-to-r from-white to-gray-50 rounded-berry p-6 border border-gray-100 shadow-berry mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Painel de Vendas</h1>
            <p className="text-gray-600 mt-1">Gráficos consolidados por período</p>
          </div>
          <div className="hidden md:block">
            <div className="text-right">
              <p className="text-sm text-gray-500">Última atualização</p>
              <p className="text-sm font-medium text-gray-900">{new Date().toLocaleString('pt-BR')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Data inicial</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input type="date" value={dataInicial} onChange={(e) => setDataInicial(e.target.value)}
                     className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Data final</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input type="date" value={dataFinal} onChange={(e) => setDataFinal(e.target.value)}
                     className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" />
            </div>
          </div>
          <div>
            <button onClick={loadData} className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2">
              <RefreshCcw className="h-4 w-4" />
              Buscar
            </button>
          </div>
        </div>
      </div>

      {/* Cards iniciais do dashboard (estilo DashboardPage) */}
      {dashboardCards.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {dashboardCards.map((c, idx) => {
            const { Icon, gradient, bgGradient } = getCardVisual(c.label);
            return (
              <div
                key={idx}
                className="group bg-white rounded-berry shadow-berry border border-gray-100 p-6 hover:shadow-berry-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-600 mb-2">{c.label}</div>
                    <div className="text-3xl font-bold text-gray-900">{c.value}</div>
                  </div>
                  <div className={`bg-gradient-to-br ${gradient} p-3 rounded-berry shadow-berry group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className={`mt-4 h-1 bg-gradient-to-r ${bgGradient} rounded-full`}></div>
              </div>
            );
          })}
        </div>
      )}

      {/* Grade: 2 colunas em tablets e telas grandes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Gráfico: Vendas por dia da semana */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Vendas por dia da semana</h2>
            {loading && <span className="text-sm text-gray-500">Carregando...</span>}
          </div>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={weekdayData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
                <Legend />
                <Bar dataKey="valor" name="Valor" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gráfico: Vendas por mês */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Vendas por mês</h2>
            {loading && <span className="text-sm text-gray-500">Carregando...</span>}
          </div>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={monthData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
                <Legend />
                <Bar dataKey="valor" name="Valor" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Grade: 2 colunas em tablets e telas grandes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Gráfico: Produtos mais vendidos */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Produtos mais vendidos</h2>
            {loading && <span className="text-sm text-gray-500">Carregando...</span>}
          </div>
          <BarChartWithMinHeight data={productData} height={350} barName="Quantidade/Valor" barColor="#10b981" />
        </div>

        {/* Gráfico: Vendas por status (pizza) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Vendas por status</h2>
            {loading && <span className="text-sm text-gray-500">Carregando...</span>}
          </div>
          <div style={{ width: '100%', height: 350 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={statusPieData} dataKey="valor" nameKey="label" cx="50%" cy="50%" innerRadius={60} outerRadius={100} label>
                  {statusPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={statusColors[entry.label] || '#6b7280'} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any) => String(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Tabela: Vendas recentes (estilo DashboardPage) */}
      <div className="bg-white rounded-berry shadow-berry border border-gray-100 p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Vendas recentes</h2>
          {loading && <span className="text-sm text-gray-500">Carregando...</span>}
        </div>
        {recentSales.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendedor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data da Venda</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentSales.slice(0, 10).map((s: any) => (
                  <tr key={s.id ?? `${s.cliente}-${s.dataVenda}`} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3 text-gray-900">
                      {s.id ? (
                        <a href={`/vendas/${s.id}`} className="text-primary-600 hover:text-primary-700 font-medium">{s.id}</a>
                      ) : '—'}
                    </td>
                    <td className="px-6 py-3 text-gray-900">{s.cliente ?? '—'}</td>
                    <td className="px-6 py-3 text-gray-900">{s.vendedor ?? '—'}</td>
                    <td className="px-6 py-3 text-gray-900">{formatCurrency(Number(s.valorTotal || 0))}</td>
                    <td className="px-6 py-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border border-gray-200 bg-gray-50 text-gray-700">
                        {s.status ?? '—'}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-gray-900">{s.dataVenda ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-sm text-gray-500">Nenhuma venda recente no período.</div>
        )}
      </div>
    </div>
  );
};

export default SalesDashboardPage;
